import { Switch, TAB_TYPE, Tab, useToast } from '@repo/design-system/index'
import {
  QIcon_bell_default,
  QIcon_bell_off,
  QIcon_bookmark,
  QIcon_chevron_bold_left,
  QIcon_close_default,
} from '@repo/icons/index'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { format, isValid } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import apis from '../../../../modules/apis'
import { tutorProfileQueryApi, useTutorProfile } from '../../../../modules/apis/student/tutors/queries'
import { axiosPost } from '../../../../modules/axiosHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_tutors, s_portal_home } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { saveTutor } from '../../../../modules/schedule/fetches'
import { COLORS } from '../../../../modules/vars'
import { useSidePopup } from '../../../basic/popup/SidePopup'
import CompletedLesson from '../../../new-portal/lessons/schedules/CompletedLesson'
import ReviewsPanel from './ReviewsPanel'
import SelfIntroPanel from './SelfIntroPanel'
import SummaryPanel from './SummaryPanel'
import { TUTOR_PROFILE_TAB } from './TutorBody'
import TutorInfoCard from './TutorInfoCard'
import TutorScheduleLesson from './TutorScheduleLesson'
import TutorVideo from './TutorVideo'
import useTutorNotification from './useTutorNotification'

const TutorDetailBar = ({ setTab, data, refetch }) => {
  const { t, locale } = useTranslation()
  const router = useAppRouter()
  const [saved, setSaved] = useState(data?.is_mine)
  const { toastInfo } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: saveTutor,
    onSuccess: () => {
      setSaved(!saved)
      queryClient.invalidateQueries(tutorProfileQueryApi.getQueryKey({ searchParams: { tutor_id: data.id, locale } }))
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const handleSave = async (e: React.MouseEvent, saved) => {
    if (mutation.isLoading) return
    toastInfo(saved ? t(s_portal_home.tutor_like_added) : t(s_portal_home.tutor_like_deleted))
    mutation.mutate({ tutorId: data.id, isAdd: saved })
  }

  const { updateNotificationStatus } = useTutorNotification()

  return (
    <div className="relative flex h-[54px] w-screen items-center justify-between bg-white px-[20px]">
      <div className="Body1-16Md absolute left-0 right-0 mx-auto w-full text-center">{data?.name}</div>
      <div onClick={() => router.back()} className="z-10">
        <QIcon_chevron_bold_left width={20} height={20} className="text-black" />
      </div>
      <div className="z-10 flex gap-x-[12px]">
        {saved && (
          <button onClick={() => updateNotificationStatus(data?.id, !data.tutor_notification, refetch)}>
            {data.tutor_notification ? (
              <QIcon_bell_default width={24} height={24} color={COLORS.GRAY700} />
            ) : (
              <QIcon_bell_off width={24} height={24} color={COLORS.GRAY300} />
            )}
          </button>
        )}
        <div onClick={(e) => handleSave(e, !saved)}>
          <QIcon_bookmark width={24} height={24} color={saved ? COLORS.RED400 : COLORS.GRAY300} />
        </div>
      </div>
    </div>
  )
}

export default function TutorSideModalBody({
  id,
  isApp = false,
  initialTab = TUTOR_PROFILE_TAB.SUMMARY,
  isFeaturedTutor = false,
  setShowBottomSheet = null,
  result = null,
  setResult = null,
}) {
  const router = useAppRouter()
  const { t, locale, isLocaleKO } = useTranslation()
  const [activeId, setActiveId] = useState(initialTab)
  const { data, isFetching, refetch } = useTutorProfile({ tutor_id: id, locale })
  const sidePopup = useSidePopup()
  const [saved, setSaved] = useState(data?.my_tutor)
  const { toastInfo } = useToast()
  const isMobile = useIsMobile()
  const isSMSize = useIsMobile(undefined, 1040)
  const timeDiv = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (data) {
      setSaved(data?.my_tutor)
    }
  }, [data])

  useEffect(() => {
    setActiveId(initialTab)
  }, [initialTab])

  const isSchedule = useMemo(() => router.asPath.includes('/schedule'), [router.asPath])

  const tabs = [
    { id: TUTOR_PROFILE_TAB.SUMMARY, name: t(n_tutors.profile_overview), none: false },
    { id: TUTOR_PROFILE_TAB.SELF_INTRO, name: t(n_tutors.intro), none: false },
    { id: TUTOR_PROFILE_TAB.REVIEW, name: t(n_tutors.review), none: false },
    { id: TUTOR_PROFILE_TAB.SCHEDULE, name: t(n_tutors.book_lessons), none: isSchedule },
  ]

  const message = data?.announcement?.message
  const [notice, setNotice] = useState(message)
  const [showKorean, setShowKorean] = useState(false)

  const triggerChange = async (content) => {
    const response = await axiosPost(apis.googleTranslate(), {
      text: content,
      mode: 'en_ko',
    })
    if (response.success) {
      setNotice(response.translated)
    } else {
      alert(response.message)
    }
  }

  useEffect(() => {
    if (message && !showKorean) {
      setNotice(message)
    }
  }, [message, showKorean])

  useEffect(() => {
    if (message && showKorean) {
      triggerChange(message)
    }
  }, [showKorean])

  const mutation = useMutation({
    mutationFn: saveTutor,
    onSuccess: () => {
      setSaved(saved)
      queryClient.invalidateQueries(tutorProfileQueryApi.getQueryKey({ searchParams: { tutor_id: id, locale } }))
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const updated_at = data?.announcement?.updated_at

  const onCloseSidePopup = () => {
    sidePopup.close('tutor-popup')
  }

  const onCloseBottomSheet = (e) => {
    e.stopPropagation()
    setShowBottomSheet && setShowBottomSheet(false)
  }

  const handleSave = (e, isSaved, showToast = true, onClickHeart = undefined) => {
    if (onClickHeart) {
      onClickHeart(e, data)
      setSaved(isSaved)
      if (showToast) {
        toastInfo(saved ? t(s_portal_home.tutor_like_deleted) : t(s_portal_home.tutor_like_added))
      }
      return
    }

    if (mutation.isLoading) return

    if (showToast) {
      toastInfo(saved ? t(s_portal_home.tutor_like_deleted) : t(s_portal_home.tutor_like_added))
    }

    mutation.mutate({ tutorId: data.id, isAdd: isSaved })
  }

  const { updateNotificationStatus } = useTutorNotification()

  if (isFetching) return <div></div>
  return (
    <div className={classNames('relative w-full', [isSMSize && !isApp ? 'h-[600px]' : 'h-full'])}>
      {result ? (
        <div className="w-full max-w-[976px] py-[32px] px-[16px] mx-auto">
          <CompletedLesson result={result} />
        </div>
      ) : (
        <div className="relative h-full w-full">
          <div
            className={classNames('top-[0px] z-[9999] w-full', {
              sticky: isApp && isSchedule,
              fixed: !(isApp && isSchedule),
            })}
          >
            {isApp && !isSchedule && <TutorDetailBar setTab={setActiveId} data={data} refetch={refetch} />}
            {isApp && isSchedule && (
              <div className="flex w-full flex-row items-center justify-between bg-white px-[16px] py-[14px]">
                <div className="flex items-center">
                  <img className="h-[40px] w-[40px] rounded-full object-cover" src={data.image_url} />
                  <div className="Headline3-22Bd ml-[16px]">{data.name}</div>
                </div>
                <div className="flex flex-row space-x-[12px]">
                  {saved && (
                    <button onClick={() => updateNotificationStatus(data?.id, !data.tutor_notification)}>
                      {data.tutor_notification ? (
                        <QIcon_bell_default width={24} height={24} color={COLORS.GRAY700} />
                      ) : (
                        <QIcon_bell_off width={24} height={24} color={COLORS.GRAY300} />
                      )}
                    </button>
                  )}
                  <QIcon_bookmark
                    onClick={(e) => handleSave(e, !saved, true, undefined)}
                    className="h-24 w-24 cursor-pointer"
                    color={saved ? COLORS.RED400 : COLORS.GRAY300}
                  />
                  <QIcon_close_default
                    className="h-24 w-24 cursor-pointer"
                    onClick={isSMSize ? onCloseBottomSheet : onCloseSidePopup}
                  />
                </div>
              </div>
            )}
            {!isApp && (
              <div className="flex w-full flex-row items-center justify-between bg-white px-[16px] py-[14px]">
                <div className="flex items-center">
                  <img className="h-[40px] w-[40px] rounded-full object-cover" src={data.image_url} />
                  <div className="Headline3-22Bd ml-[16px]">{data.name}</div>
                </div>
                <div className="flex flex-row space-x-[12px]">
                  {saved && (
                    <button onClick={() => updateNotificationStatus(data?.id, !data.tutor_notification)}>
                      {data.tutor_notification ? (
                        <QIcon_bell_default width={24} height={24} color={COLORS.GRAY700} />
                      ) : (
                        <QIcon_bell_off width={24} height={24} color={COLORS.GRAY300} />
                      )}
                    </button>
                  )}
                  <QIcon_bookmark
                    onClick={(e) => handleSave(e, !saved, true, undefined)}
                    className="h-24 w-24 cursor-pointer"
                    color={saved ? COLORS.RED400 : COLORS.GRAY300}
                  />
                  <QIcon_close_default
                    className="h-24 w-24 cursor-pointer"
                    onClick={isMobile ? onCloseBottomSheet : onCloseSidePopup}
                  />
                </div>
              </div>
            )}
            {/* 하단 탭 섹션 */}
            <div className="px-[0px]">
              <Tab
                className="h-[52px] bg-white px-[16px] lg:mx-[0px]"
                type={TAB_TYPE.SCROLL}
                activeTabIdx={activeId}
                setActiveTabIdx={setActiveId}
              >
                {tabs
                  .filter((tab) => !tab.none)
                  .map((tab, i) => {
                    return <Tab.TabItem key={i} name={tab.name}></Tab.TabItem>
                  })}
              </Tab>
            </div>
          </div>

          <div
            className={classNames('w-full', {
              'pt-[120px]': !isApp,
              'pt-[106px]': isApp && !isSchedule,
              'pt-[16px]': isApp && isSchedule,
            })}
          >
            <div className="py-[16px]">
              <div ref={timeDiv}>
                {activeId == TUTOR_PROFILE_TAB.SUMMARY && (
                  <>
                    <div className="w-full px-[16px]">
                      <div className="flex flex-col justify-center">
                        <TutorVideo data={data} />
                        <div>
                          <TutorInfoCard
                            data={data}
                            setActiveId={setActiveId}
                            isFeaturedTutor={isFeaturedTutor}
                            isModal={true}
                          />
                          {message && (
                            <div>
                              <div className="h-[1px] w-full bg-gray-200"></div>
                              <div className="mt-[16px]">
                                <div className="mb-[8px] flex flex-row items-center justify-between">
                                  <div className=" flex items-center">
                                    <div className="Body1-16Bd">{t(n_tutors.annoucement)}</div>
                                    <div className="Body2-14Md ml-[8px] text-gray-500">
                                      {isValid(new Date(updated_at)) && format(new Date(updated_at), 'yyyy.MM.dd')}
                                    </div>
                                  </div>
                                  <div className="">
                                    {isLocaleKO && (
                                      <div className="">
                                        <div className="flex flex-row items-center justify-end">
                                          <span className="Body2-14Md mr-[8px] text-gray-500">한글로 보기</span>
                                          <Switch checked={showKorean} setChecked={setShowKorean} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="Body2-14Md rounded-[8px] bg-gray-100 p-[16px] text-gray-700">
                                  {notice}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="my-[16px] h-[8px] w-full border-t-[1px] border-gray-100 bg-gray-50"></div>
                    <div className="px-[16px]">
                      <SummaryPanel data={data} setActiveTab={setActiveId} />
                    </div>
                  </>
                )}
                {activeId == TUTOR_PROFILE_TAB.SELF_INTRO && <SelfIntroPanel data={data} teens={false} />}
                {activeId == TUTOR_PROFILE_TAB.REVIEW && <ReviewsPanel data={data} />}
                {activeId == TUTOR_PROFILE_TAB.SCHEDULE && data && (
                  <TutorScheduleLesson tutor={data} setResult={setResult} size="sm" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
